import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { Option, Image } from 'src/types';
import LightBox, { LightBoxItem } from 'src/components/LightBox';
import GridPhotoPopup from 'src/components/option/GridPhotoPopup';
import Player from '@vimeo/player';
import classNames from 'classnames';
import VideoCard from 'src/components/VideoCard';
import style from 'src/styles/modules/components/option_media.module.scss';
import useIsVimeoSupportedCountry from 'src/hooks/useIsVimeoSupportedCountry';
import { enableBodyScroll, disableBodyScroll } from 'body-scroll-lock';
import useBreakpoints from 'src/hooks/useBreakpoints';
import useIsMounted from 'src/hooks/useIsMounted';

type OptionMediaItemProps = {
  videoClassName?: string;
  media: string | Image;
  setPlayers: React.Dispatch<React.SetStateAction<Player[]>>;
  setShowAllPhotoPopup: React.Dispatch<React.SetStateAction<boolean>>;
};


type ImageFieldName =
  | 'url'
  | 'xlUrl'
  | 'lgUrl'
  | 'mdUrl'
  | 'smUrl'
  | 'webpXlUrl'
  | 'webpLgUrl'
  | 'webpMdUrl'
  | 'webpSmUrl'
  | 'blurUrl';
type ImageProps = {
  record: Image;
  imgField: ImageFieldName;
  alt: string;
  fill: boolean;
  className: string;
  onClick?: () => void;
};

const ImageWithFallback: React.FC<ImageProps> = ({ record, imgField, onClick, className }) => {
  return (
    <img
      src={record[imgField]}
      alt=""
      className={`tw-rounded-lg tw-border-2 tw-border-brand-green tw-border-solid tw-absolute tw-top-0 tw-left-0 ${className || ''}`}
      onClick={() => onClick && onClick()}
    />
  );
};

const OptionMediaItem: React.FC<OptionMediaItemProps> = ({
  media,
  setPlayers,
  setShowAllPhotoPopup,
  videoClassName
}) => {
  return typeof media === 'string' ? (
    <div className={`option-video tw-h-full tw-relative tw-rounded-lg tw-border-2 tw-border-brand-green ${videoClassName || ''}`}>
      <VideoCard vimeoId={media} setPlayers={setPlayers} vimeoConfig={{ autoplay: 1 }} />
    </div>
  ) : (
    <ImageWithFallback
      record={media as Image}
      imgField="webpLgUrl"
      alt=""
      fill
      className="tw-object-cover tw-w-full tw-h-full"
      onClick={() => {
        setShowAllPhotoPopup(true);
      }}
    />
  );
};

type Props = {
  option: Option;
};

const OptionMedia: React.FC<Props> = ({ option }) => {
  const gridPhotoPopupContainerRef = useRef<HTMLDivElement>(null);
  const [players, setPlayers] = useState<Player[]>([]);
  const canShowVideo = useIsVimeoSupportedCountry();
  const [showOptionMediaLightBox, setShowOptionMediaLightBox] = useState(false);
  const [showAllPhotoPopup, setShowAllPhotoPopup] = useState(false);
  const [slideId, setSlideId] = useState<string>();
  const mediaAssets: (string | Image)[] = useMemo(
    () => [...(canShowVideo ? option.vimeoIds : []), ...option.images],
    [option.id]
  );

  const isMounted = useIsMounted()
  const { isScreenMd } = useBreakpoints()
  const images = option.images

  const mediaAssetsWithOneVideo: (string | Image)[] = useMemo(
    () => {
      const assets: (string | Image)[] = [...images]
      if (canShowVideo) {
        const index = images.length >= 5 ? 4 : images.length
        option.vimeoIds.slice(0, 1).forEach((vimeoId) => {
          assets.splice(index, 0, vimeoId)
        })
      }
      return assets
    },
    [option.id]
  );

  const onClosePopup = useCallback(() => {
    setShowAllPhotoPopup(false)
    players.map((player) => player.pause());
  }, [players]);

  const onOpenPopup = useCallback(() => {
    setShowAllPhotoPopup(true)
    players.map((player) => player.pause());
  }, [players]);

  const onClickPopupImage = useCallback((image: Image) => {
    setSlideId(`option-image-${image.id}`);
    setShowOptionMediaLightBox(true);
    players.map((player) => player.pause());
  }, [players]);

  const lightboxItems: LightBoxItem[] = mediaAssets.map((o) => {
    if (typeof o === 'string') {
      return {
        id: o,
        vimeoId: o,
      };
    } else {
      return {
        id: `option-image-${o.id}`,
        imageUrl: o.webpLgUrl,
        smImageUrl: o.webpSmUrl,
        caption: o.caption,
      };
    }
  });

  useEffect(() => {
    if (showAllPhotoPopup) {
      if (gridPhotoPopupContainerRef.current) {
        disableBodyScroll(gridPhotoPopupContainerRef.current);
      }
    } else {
      if (gridPhotoPopupContainerRef.current) {
        enableBodyScroll(gridPhotoPopupContainerRef.current);
      }
    }
  }, [setShowAllPhotoPopup]);

  useEffect(() => {
    if (showAllPhotoPopup) {
      players.map((player) => player.pause());
    } else {
      players.map((player) => player.play());
    }
  }, [showAllPhotoPopup]);

  return mediaAssets.length > 0 ? (
    <>
      <div className="tw-w-full tw-mx-auto tw-relative">
        <div className={classNames(`tw-w-full`)}>
          {mediaAssetsWithOneVideo.length === 1 ? (
            <div
              className={classNames(`tw-w-full tw-relative tw-cursor-pointer ${style.baseMediaHeight}`)}
              onClick={() => {
                if (typeof mediaAssetsWithOneVideo[0] === 'string') return
                onOpenPopup()
              }}
            >
              <OptionMediaItem
                media={mediaAssetsWithOneVideo[0]}
                setPlayers={setPlayers}
                setShowAllPhotoPopup={setShowAllPhotoPopup}
              />
            </div>
          ) : mediaAssetsWithOneVideo.length > 1 ? (
            <div className="tw-flex tw-items-center tw-overflow-x-auto tw-overflow-y-hidden custom-scrollbar tw--ml-2 tw--mr-3.75 lg:tw--mx-2 tw-pb-2">
              <div
                className={classNames(
                  `tw-px-2 ${style.baseMediaWidth} tw-flex-shrink-0 ${style.baseMediaHeight}`
                )}
              >
                <div className="tw-w-full tw-h-full tw-relative tw-cursor-pointer">
                  <OptionMediaItem
                    media={mediaAssetsWithOneVideo[0]}
                    setPlayers={setPlayers}
                    setShowAllPhotoPopup={setShowAllPhotoPopup}
                  />
                </div>
              </div>
              {mediaAssetsWithOneVideo.length === 2 ? (
                <div
                  className={classNames(
                    `tw-px-2 ${isMounted ? typeof mediaAssetsWithOneVideo[1] === 'string' && !isScreenMd ? style.baseMediaWidth : style.xlMediaWidth : style.baseMediaWidth} tw-flex-shrink-0 ${style.baseMediaHeight}`
                  )}
                >
                  <div
                    className="tw-w-full tw-h-full tw-relative tw-cursor-pointer"
                    onClick={() => {
                      if (typeof mediaAssetsWithOneVideo[1] === 'string') return
                      onOpenPopup()
                    }}
                  >
                    <OptionMediaItem
                      media={mediaAssetsWithOneVideo[1]}
                      setPlayers={setPlayers}
                      setShowAllPhotoPopup={setShowAllPhotoPopup}
                      videoClassName="option-video-no-expand"
                    />
                  </div>
                </div>
              ) : mediaAssetsWithOneVideo.length === 3 ? (
                <>
                  <div
                    className={classNames(
                      `tw-px-2 ${style.lgMediaWidth} tw-flex-shrink-0 ${style.baseMediaHeight}`
                    )}
                  >
                    <div
                      className="tw-w-full tw-h-full tw-relative tw-cursor-pointer"
                      onClick={onOpenPopup}
                    >
                      <OptionMediaItem
                        media={mediaAssetsWithOneVideo[1]}
                        setPlayers={setPlayers}
                        setShowAllPhotoPopup={setShowAllPhotoPopup}
                      />
                    </div>
                  </div>
                  <div
                    className={classNames(
                      `tw-px-2 ${style.baseMediaWidth} tw-flex-shrink-0 ${style.baseMediaHeight}`
                    )}
                  >
                    <div
                      className="tw-w-full tw-h-full tw-relative tw-cursor-pointer"
                      onClick={() => {
                        if (typeof mediaAssetsWithOneVideo[2] === 'string') return
                        onOpenPopup()
                      }}
                    >
                      <OptionMediaItem
                        media={mediaAssetsWithOneVideo[2]}
                        setPlayers={setPlayers}
                        setShowAllPhotoPopup={setShowAllPhotoPopup}
                        videoClassName="option-video-no-expand"
                      />
                    </div>
                  </div>
                </>
              ) : mediaAssetsWithOneVideo.length === 4 ? (
                <>
                  <div
                    className={classNames(
                      `tw-px-2 ${style.baseMediaWidth} tw-flex-shrink-0 tw-relative ${style.baseMediaHeight}`
                    )}
                  >
                    <div className={`tw-pb-2 ${style.smMediaHeight}`}>
                      <div
                        className="tw-w-full tw-h-full tw-relative tw-cursor-pointer"
                        onClick={onOpenPopup}
                      >
                        <OptionMediaItem
                          media={mediaAssetsWithOneVideo[1]}
                          setPlayers={setPlayers}
                          setShowAllPhotoPopup={setShowAllPhotoPopup}
                        />
                      </div>
                    </div>
                    <div className={`tw-pt-2 ${style.smMediaHeight}`}>
                      <div
                        className="tw-w-full tw-h-full tw-relative tw-cursor-pointer"
                        onClick={onOpenPopup}
                      >
                        <OptionMediaItem
                          media={mediaAssetsWithOneVideo[2]}
                          setPlayers={setPlayers}
                          setShowAllPhotoPopup={setShowAllPhotoPopup}
                        />
                      </div>
                    </div>
                  </div>
                  <div
                    className={classNames(
                      `tw-px-2 ${isMounted ? typeof mediaAssetsWithOneVideo[3] === 'string' && !isScreenMd ? style.baseMediaWidth : style.lgMediaWidth : style.baseMediaWidth} tw-flex-shrink-0 ${style.baseMediaHeight}`
                    )}
                  >
                    <div
                      className="tw-w-full tw-h-full tw-relative tw-cursor-pointer"
                      onClick={() => {
                        if (typeof mediaAssetsWithOneVideo[3] === 'string') return
                        onOpenPopup()
                      }}
                    >
                      <OptionMediaItem
                        media={mediaAssetsWithOneVideo[3]}
                        setPlayers={setPlayers}
                        setShowAllPhotoPopup={setShowAllPhotoPopup}
                        videoClassName="option-video-no-expand"
                      />
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div
                    className={classNames(
                      `tw-px-2 tw-relative ${style.baseMediaWidth} tw-flex-shrink-0 ${style.baseMediaHeight}`
                    )}
                  >
                    <div className={`tw-pb-2 ${style.smMediaHeight}`}>
                      <div
                        className="tw-w-full tw-h-full tw-relative tw-cursor-pointer"
                        onClick={onOpenPopup}
                      >
                        <OptionMediaItem
                          media={mediaAssetsWithOneVideo[1]}
                          setPlayers={setPlayers}
                          setShowAllPhotoPopup={setShowAllPhotoPopup}
                        />
                      </div>
                    </div>
                    <div className={`tw-pt-2 ${style.smMediaHeight}`}>
                      <div
                        className="tw-w-full tw-h-full tw-relative tw-cursor-pointer"
                        onClick={onOpenPopup}
                      >
                        <OptionMediaItem
                          media={mediaAssetsWithOneVideo[2]}
                          setPlayers={setPlayers}
                          setShowAllPhotoPopup={setShowAllPhotoPopup}
                        />
                      </div>
                    </div>
                  </div>
                  <div
                    className={classNames(
                      `tw-px-2 ${style.baseMediaWidth} tw-flex-shrink-0 ${style.baseMediaHeight}`
                    )}
                  >
                    <div
                      className="tw-w-full tw-h-full tw-relative tw-cursor-pointer"
                      onClick={onOpenPopup}
                    >
                      <OptionMediaItem
                        media={mediaAssetsWithOneVideo[3]}
                        setPlayers={setPlayers}
                        setShowAllPhotoPopup={setShowAllPhotoPopup}
                      />
                    </div>
                  </div>
                  <div
                    className={classNames(
                      `tw-px-2 ${style.baseMediaWidth} tw-flex-shrink-0 tw-relative ${style.baseMediaHeight}`
                    )}
                  >
                    <div
                      className="tw-w-full tw-h-full tw-relative tw-cursor-pointer"
                      onClick={() => {
                        if (typeof mediaAssetsWithOneVideo[4] === 'string') return
                        onOpenPopup()
                      }}
                    >
                      <OptionMediaItem
                        media={mediaAssetsWithOneVideo[4]}
                        setPlayers={setPlayers}
                        setShowAllPhotoPopup={setShowAllPhotoPopup}
                        videoClassName="option-video-no-expand"
                      />
                    </div>

                    {mediaAssets.length > 5 ? (
                      <div className="tw-absolute tw-bottom-5 tw-right-8 tw-block lg:tw-hidden tw-z-50">
                        <button
                          type="button"
                          className="btn btn--white"
                          onClick={() => {
                            setShowAllPhotoPopup(true);
                          }}
                        >
                          View All
                        </button>
                      </div>
                    ) : null}
                  </div>
                </>
              )}
            </div>
          ) : null}
        </div>

        {mediaAssets.length > 5 ? (
          <div className="tw-absolute tw-bottom-6 tw-right-10 tw-hidden lg:tw-block tw-z-50">
            <button
              type="button"
              className="btn btn--white"
              onClick={() => {
                setShowAllPhotoPopup(true);
              }}
            >
              View All
            </button>
          </div>
        ) : null}
      </div>

      <LightBox
        enableNav={false}
        items={lightboxItems}
        show={showOptionMediaLightBox}
        slideId={slideId}
        onShow={() => {
          players.map((player) => player.pause());
        }}
        onClose={() => setShowOptionMediaLightBox(false)}
      />
      <div ref={gridPhotoPopupContainerRef}>
        <GridPhotoPopup
          show={showAllPhotoPopup}
          optionMedia={mediaAssets as Image[]}
          onClose={onClosePopup}
          onClickImage={onClickPopupImage}
        />
      </div>
    </>
  ) : (
    <></>
  );
};

export default OptionMedia;
